import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import { X, Trash2, Check, Plus, ArrowLeft, Edit, Settings, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
// Rename the motion import to avoid conflict with Motion type
import { motion as framerMotion } from "framer-motion";
import { fetchWithAuth, getApiBaseUrl } from '../utils/api';
// Import the function but not the types
import { parseMotionsFromResponse } from '../utils/helpers';
import { TableContainer } from '../components/ui/table-container';
import { toast } from "react-hot-toast";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter 
} from "src/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog";

// Use the local interface definitions
interface Theme {
  id: number;
  name: string;
  description?: string;
}

// Interface is unused but might be needed in future, so we'll keep it but disable eslint warning
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Competition {
  id: number;
  name: string;
  description?: string;
}

// Update the Motion interface to include all the properties we're using
interface Motion {
  id: number;
  theme: Theme | string;
  theme_id?: number;
  theme_name?: string;
  text: string;
  info_slide?: string;
  difficulty?: string;
  difficulty_display?: string;
  created_at: string;
  competition_type: string;
  new_competition_name?: string;
  competition_id?: string;
}

// Update the initialNewMotion object
const initialNewMotion = {
  theme_name: "",
  text: "",
  theme_id: "",
  info_slide: "",
  difficulty: "intermediate",
  competition_type: "General",
  competition_id: "",
  new_competition_name: ""
};

const MotionsPanel: React.FC = () => {
  const navigate = useNavigate();
  const [motions, setMotions] = useState<Motion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNewMotionForm, setShowNewMotionForm] = useState(false);
  const [newMotion, setNewMotion] = useState(initialNewMotion);
  const [submitting, setSubmitting] = useState(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [competitions, setCompetitions] = useState<string[]>([]);
  const [themeInputMode, setThemeInputMode] = useState<'select' | 'create'>('select');
  const [competitionInputMode, setCompetitionInputMode] = useState<'select' | 'create'>('select');
  
  // Add state for theme management dialog
  const [showThemeManagementDialog, setShowThemeManagementDialog] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");
  
  // Add state for competition management dialog
  const [showCompetitionManagementDialog, setShowCompetitionManagementDialog] = useState(false);
  const [newCompetitionName, setNewCompetitionName] = useState("");
  
  // Add state for motion editing
  const [editingMotion, setEditingMotion] = useState<Motion | null>(null);
  const [showEditMotionDialog, setShowEditMotionDialog] = useState(false);
  
  // Add state for motion selection
  const [selectedMotion, setSelectedMotion] = useState<Motion | null>(null);
  
  // Add state for delete confirmation dialog
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [motionToDelete, setMotionToDelete] = useState<Motion | null>(null);
  const [deletingMotion, setDeletingMotion] = useState(false);
  
  // Add state for search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTheme, setSelectedTheme] = useState<string>("All Themes");
  const [selectedTag, setSelectedTag] = useState<string>("All Tags");
  const [selectedVisibility, setSelectedVisibility] = useState<string>("All");
  
  // Log when component mounts
  useEffect(() => {
    console.log("MotionsPanel mounted");
    
    // Check auth status
    const token = localStorage.getItem("token");
    const isAdmin = localStorage.getItem("is_admin") === "true";
    console.log("Auth status in MotionsPanel:", { 
      hasToken: !!token, 
      token: token ? token.substring(0, 10) + "..." : "none",
      isAdmin 
    });
    
    fetchMotions();
    fetchThemes();
    fetchCompetitions();
  }, []);

  console.log("Current API base URL:", getApiBaseUrl());

  // Update the fetchMotions function to use the proper API endpoint
const fetchMotions = async () => {
  try {
    setLoading(true);
    console.log("Fetching motions from endpoint:", '/api/admin/motions/');
    
    // Change to /api/admin/motions/
    const data = await fetchWithAuth('/api/admin/motions/');
    console.log("Motions response data:", data);
    
    // Make sure you're handling the response structure correctly
    setMotions(parseMotionsFromResponse(data));
    setError("");
  } catch (err: any) {
    console.error("Error details:", err);
    setError(err.message || "An error occurred while fetching motions");
  } finally {
    setLoading(false);
  }
};

// Update the fetchThemes function to use the proper API endpoint
const fetchThemes = async () => {
  try {
    // Change from '/themes/' to '/api/themes/'
    const data = await fetchWithAuth('/api/themes/');
    setThemes(data);
  } catch (err) {
    console.error("Error fetching themes:", err);
    // Set default themes if the API fails
    setThemes([
      { id: 1, name: "Politics" },
      { id: 2, name: "Economics" },
      { id: 3, name: "International Relations" },
      { id: 4, name: "Social Issues" },
      { id: 5, name: "Philosophy" },
      { id: 6, name: "Technology" }
    ]);
  }
};

// Update fetchCompetitions to use the proper API endpoint
const fetchCompetitions = async () => {
  try {
    // Get competition types from API
    const data = await fetchWithAuth('/api/competition-types/');
    
    // If API returns competition types, use them
    if (Array.isArray(data) && data.length > 0) {
      setCompetitions(data.map(c => typeof c === 'string' ? c : c.name));
    } else {
      // If no competition types exist in backend, initialize with defaults
      console.log("No competition types found, initializing defaults...");
      const defaultCompetitions = ['General', 'WUDC', 'PDA', 'AIDA', 'ABP'];
      
      // Set competitions to default
      setCompetitions(defaultCompetitions);
    }
  } catch (err) {
    console.error("Error fetching competition types:", err);
    // Keep the default competitions only in UI if API fails
    setCompetitions(['General', 'WUDC', 'PDA', 'AIDA', 'ABP']);
  }
};

  // Update the handleAddMotion function to handle competition type creation
  const handleAddMotion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation based on theme and competition input modes
    if (!newMotion.text) {
      setError("Motion text is required");
      return;
    }
    
    if (themeInputMode === 'select' && !newMotion.theme_id) {
      setError("Please select a theme");
      return;
    }
    
    if (themeInputMode === 'create' && !newMotion.theme_name) {
      setError("Theme name is required");
      return;
    }
    
    if (competitionInputMode === 'create' && !newMotion.new_competition_name) {
      setError("Competition name is required");
      return;
    }
    
    try {
      setSubmitting(true);
      
      let themeId = newMotion.theme_id;
      let competitionType = newMotion.competition_type;
      
      // If creating a new theme
      if (themeInputMode === 'create') {
        try {
          const themeResponse = await fetchWithAuth('/api/themes/', {
            method: 'POST',
            body: JSON.stringify({
              name: newMotion.theme_name
            })
          });
          themeId = themeResponse.id;
          
          // Add the new theme to our themes list
          setThemes([...themes, themeResponse]);
          
        } catch (themeErr: any) {
          console.error("Error creating theme:", themeErr);
          throw new Error("Failed to create theme");
        }
      }
      
      // If creating a new competition type
      if (competitionInputMode === 'create') {
        competitionType = newMotion.new_competition_name;
        
        // Add to competitions list if not exists
        if (!competitions.includes(competitionType)) {
          try {
            // Create competition type in backend first
            await fetchWithAuth('/api/competition-types/', {
              method: 'POST',
              body: JSON.stringify({
                name: competitionType
              })
            });
            
            // Then update UI state after successful API call
            setCompetitions(prev => [...prev, competitionType]);
            
          } catch (compErr: any) {
            console.error("Error creating competition type:", compErr);
            throw new Error("Failed to create competition type");
          }
        }
      }
      
      // Create the motion with the theme ID
      try {
        // Ensure themeId is a valid number
        const themeIdNum = typeof themeId === 'string' ? parseInt(themeId, 10) : themeId;
        
        // Log the payload for debugging
        const payload = {
          text: newMotion.text,
          theme_id: themeIdNum,
          competition_type: competitionType,
          info_slide: newMotion.info_slide,
          difficulty: newMotion.difficulty
        };
        console.log("Sending motion payload:", payload);
        
        // Use the correct endpoint for motion creation
        console.log("Creating motion at endpoint:", '/api/admin/motions/');
        const response = await fetchWithAuth('/api/admin/motions/', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        
        // Add the new motion to the list
        setMotions([response, ...motions]);
        
        // Reset form
        setNewMotion(initialNewMotion);
        setShowNewMotionForm(false);
        
        // Show success message
        toast.success('Motion added successfully');
      } catch (apiErr: any) {
        console.error('API Error creating motion:', apiErr);
        // Try to extract more detailed error message if available
        let errorMsg = 'Failed to add motion. Please try again.';
        if (apiErr.response && apiErr.response.error) {
          errorMsg = `Error: ${apiErr.response.error}`;
        } else if (apiErr.message) {
          errorMsg = apiErr.message;
        }
        setError(errorMsg);
        throw apiErr; // Re-throw to be caught by outer catch
      }
      
    } catch (err: any) {
      console.error('Error adding motion:', err);
      setError('Failed to add motion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Add handleUpdateMotion function
  const handleUpdateMotion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMotion) return;
    
    try {
      setSubmitting(true);
      
      let themeId = editingMotion.theme_id;
      let competitionType = editingMotion.competition_type;
      
      // If creating a new theme
      if (themeInputMode === 'create' && editingMotion.theme_name) {
        try {
          const themeResponse = await fetchWithAuth('/api/themes/', {
            method: 'POST',
            body: JSON.stringify({
              name: editingMotion.theme_name
            })
          });
          themeId = themeResponse.id;
          
          // Add the new theme to our themes list
          setThemes([...themes, themeResponse]);
          
        } catch (themeErr: any) {
          console.error("Error creating theme:", themeErr);
          throw new Error("Failed to create theme");
        }
      }
      
      // If creating a new competition type
      if (competitionInputMode === 'create' && editingMotion.new_competition_name) {
        competitionType = editingMotion.new_competition_name;
        
        // Add to competitions list if not exists
        if (!competitions.includes(competitionType)) {
          try {
            // Create competition type in backend first
            await fetchWithAuth('/api/competition-types/', {
              method: 'POST',
              body: JSON.stringify({
                name: competitionType
              })
            });
            
            // Then update UI state
            setCompetitions(prev => [...prev, competitionType]);
          } catch (compErr: any) {
            console.error("Error creating competition type:", compErr);
            throw new Error("Failed to create competition type");
          }
        }
      }
      
      // Update the motion
      try {
        // Ensure themeId is a valid number
        const themeIdNum = typeof themeId === 'string' ? parseInt(themeId, 10) : themeId;
        
        // Log the payload for debugging
        const payload = {
          text: editingMotion.text,
          theme_id: themeIdNum,
          competition_type: competitionType,
          info_slide: editingMotion.info_slide,
          difficulty: editingMotion.difficulty
        };
        console.log("Sending update payload:", payload);
        
        // Change endpoint to /api/admin/motions/
        await fetchWithAuth(`/api/admin/motions/${editingMotion.id}/`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        
        // Rest of the update logic...
      } catch (apiErr: any) {
        console.error('API Error updating motion:', apiErr);
        let errorMsg = 'Failed to update motion. Please try again.';
        if (apiErr.response && apiErr.response.error) {
          errorMsg = `Error: ${apiErr.response.error}`;
        } else if (apiErr.message) {
          errorMsg = apiErr.message;
        }
        setError(errorMsg);
        throw apiErr; // Re-throw to be caught by outer catch
      }
      
      // Update the motion in the list
      const updatedMotions = motions.map(motion => 
        motion.id === editingMotion.id 
          ? {
              ...motion,
              text: editingMotion.text,
              theme: typeof motion.theme === 'object' && themeId === motion.theme.id 
                ? motion.theme 
                : themes.find(t => t.id === themeId) || { id: themeId, name: 'Unknown Theme' },
              competition_type: competitionType
            } as Motion  // Add this type assertion
          : motion
      );
      setMotions(updatedMotions);
      
      setShowEditMotionDialog(false);
      setEditingMotion(null);
      
      // Show success message
      toast.success('Motion updated successfully');
      
    } catch (err: any) {
      console.error('Error updating motion:', err);
      setError('Failed to update motion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Update handleDeleteMotion to use the correct endpoint
const handleDeleteMotion = async (id: number) => {
  setShowDeleteAlert(true);
};

// Add the actual delete motion function
const confirmDeleteMotion = async () => {
  if (!motionToDelete) return;
  
  try {
    setDeletingMotion(true);
    
    // Get API base URL - FIXED: Remove '/api' from the end of baseUrl
    const baseUrl = getApiBaseUrl().replace(/\/api$/, '');
    const url = `${baseUrl}/api/admin/motions/${motionToDelete.id}/`;
    
    console.log("Deleting motion with URL:", url); // Add logging
    
    // Get token from localStorage
    const token = localStorage.getItem("token");
    
    // Make the DELETE request directly instead of using fetchWithAuth
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    // Check if response is ok (status in 200-299 range)
    if (!response.ok) {
      // If there's a response body, try to parse it for error info
      let errorMessage = `Failed with status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.detail || errorData.message || errorMessage;
      } catch (parseErr) {
        // If no JSON response, use status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // For DELETE requests, we don't need to parse the response body
    // Just check if the status is 204 (No Content) or 200 (OK)
    if (response.status === 204 || response.status === 200) {
      // Success - update UI
      setMotions(motions.filter(motion => motion.id !== motionToDelete.id));
      setError("");
      toast.success('Motion deleted successfully');
    } else {
      // Unexpected status code
      throw new Error(`Unexpected response status: ${response.status}`);
    }
    
    // Clear the selected motion if it was the one deleted
    if (selectedMotion?.id === motionToDelete.id) {
      setSelectedMotion(null);
    }
  } catch (err: any) {
    setError(err.message || "An error occurred while deleting the motion");
    console.error("Error deleting motion:", err);
    toast.error('Failed to delete motion');
  } finally {
    setDeletingMotion(false);
    setShowDeleteAlert(false);
    setMotionToDelete(null);
  }
};

  // Add handleDeleteTheme function
  const handleDeleteTheme = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this theme? This will affect all motions using this theme.")) {
      return;
    }
    
    try {
      // Change endpoint from /themes/ to /api/themes/
      await fetchWithAuth(`/api/themes/${id}/`, {
        method: "DELETE"
      });
      
      // Remove from state
      setThemes(themes.filter(theme => theme.id !== id));
      
      // Refresh motions as they might be affected
      fetchMotions();
      
      toast.success('Theme deleted successfully');
    } catch (err: any) {
      console.error("Error deleting theme:", err);
      toast.error('Failed to delete theme. It may be in use by motions.');
    }
  };
  
  // Add handleAddTheme function
  const handleAddTheme = async () => {
    if (!newThemeName) {
      toast.error('Theme name is required');
      return;
    }
    
    try {
      // Change endpoint from /themes/ to /api/themes/
      const response = await fetchWithAuth('/api/themes/', {
        method: 'POST',
        body: JSON.stringify({
          name: newThemeName
        })
      });
      
      setThemes([...themes, response]);
      setNewThemeName('');
      toast.success('Theme added successfully');
    } catch (err) {
      console.error("Error adding theme:", err);
      toast.error('Failed to add theme');
    }
  };
  
  // Update handleDeleteCompetition to use the correct endpoint
const handleDeleteCompetition = async (name: string) => {
  if (!window.confirm("Are you sure you want to delete this competition type? This will affect all motions using this type.")) {
    return;
  }
  
  try {
    // Change endpoint from /competition-types/ to /api/competition-types/
    await fetchWithAuth(`/api/competition-types/${name}/`, {
      method: "DELETE"
    }).catch(() => {
      // Silently fail if endpoint doesn't exist
    });
    
    // Remove from state
    setCompetitions(competitions.filter(comp => comp !== name));
    toast.success('Competition type deleted successfully');
  } catch (err: any) {
    console.error("Error deleting competition type:", err);
    toast.error('Failed to delete competition type');
  }
};

// Update handleAddCompetition to use the correct endpoint
const handleAddCompetition = async () => {
  if (!newCompetitionName) {
    toast.error('Competition name is required');
    return;
  }
  
  try {
    // First check if competition already exists
    if (competitions.includes(newCompetitionName)) {
      toast.error('This competition type already exists');
      return;
    }
    
    // Try to add to API
    try {
      // Change endpoint from /competition-types/ to /api/competition-types/
      await fetchWithAuth('/api/competition-types/', {
        method: 'POST',
        body: JSON.stringify({
          name: newCompetitionName
        })
      });
      
      // Add to state
      setCompetitions([...competitions, newCompetitionName]);
      setNewCompetitionName('');
      toast.success('Competition type added successfully');
    } catch (apiErr: any) {
      console.error("API Error creating competition type:", apiErr);
      throw apiErr;
    }
  } catch (err) {
    console.error("Error adding competition type:", err);
    toast.error('Failed to add competition type');
  }
};

  // Animation variants for the container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  // Animation variants for each element
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  // Navigate back to dashboard
  const handleBack = () => {
    navigate("/admin/dashboard");
  };

  // Handle motion selection
  const handleMotionSelect = (motion: Motion) => {
    setSelectedMotion(motion);
  };
  
  // Filter motions based on search query and filters
  const filteredMotions = motions.filter(motion => {
    // Search text filtering
    const matchesSearch = searchQuery 
      ? motion.text.toLowerCase().includes(searchQuery.toLowerCase()) 
      : true;
    
    // Theme filtering
    const matchesTheme = selectedTheme === "All Themes" 
      ? true 
      : (typeof motion.theme === 'object' 
          ? motion.theme.name === selectedTheme 
          : motion.theme === selectedTheme);
    
    // Tag/competition filtering
    const matchesTag = selectedTag === "All Tags" 
      ? true 
      : motion.competition_type === selectedTag;
    
    // Visibility filtering (not implemented in backend, so always true for now)
    const matchesVisibility = true;
    
    return matchesSearch && matchesTheme && matchesTag && matchesVisibility;
  });

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 flex justify-between items-center border-b border-slate-700">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBack}
            className="mr-2"
          >
            <ArrowLeft size={16} />
          </Button>
          <h1 className="text-2xl font-bold">Motion Management</h1>
        </div>
        <Button 
          onClick={() => setShowNewMotionForm(!showNewMotionForm)}
          variant="action"
        >
          <Plus size={16} className="mr-2" />
          <span>Create New Motion</span>
        </Button>
      </div>
      
      <div className="p-4 border-b border-slate-700">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Search motions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search size={16} />
            </span>
            <Button
              className="absolute right-0 top-0 h-full rounded-l-none flex items-center justify-center"
              variant="blue"
              type="submit"
            >
              <span className="inline-block align-middle">Search</span>
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">Theme:</span>
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
              >
                <option value="All Themes">All Themes</option>
                {themes.map(theme => (
                  <option key={theme.id} value={theme.name}>{theme.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm">Tag:</span>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
              >
                <option value="All Tags">All Tags</option>
                {competitions.map(comp => (
                  <option key={comp} value={comp}>{comp}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm">Visibility:</span>
              <select
                value={selectedVisibility}
                onChange={(e) => setSelectedVisibility(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm"
              >
                <option value="All">All</option>
                <option value="Public">Public</option>
                <option value="Private">Private</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        <div className="w-full md:w-5/12 border-r border-slate-700 overflow-auto">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-xl font-bold">Motions ({filteredMotions.length})</h2>
          </div>
          
          <div className="divide-y divide-slate-700">
            {loading ? (
              <div className="p-4 text-center">Loading motions...</div>
            ) : filteredMotions.length === 0 ? (
              <div className="p-4 text-center text-slate-400">
                No motions found matching the current filters.
              </div>
            ) : (
              filteredMotions.map(motion => (
                <div 
                  key={motion.id}
                  className={`p-4 cursor-pointer hover:bg-slate-800 transition-colors ${selectedMotion?.id === motion.id ? 'bg-slate-800' : ''}`}
                  onClick={() => handleMotionSelect(motion)}
                >
                  <div className="flex justify-between">
                    <div>
                      <p className="line-clamp-2">{motion.text}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-900 text-blue-200">
                          {typeof motion.theme === 'string' ? motion.theme : motion.theme.name}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-700 text-gray-200">
                          {motion.competition_type}
                        </span>
                        {motion.difficulty && (
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium 
                            ${motion.difficulty === 'beginner' ? 'bg-green-900 text-green-200' : 
                            motion.difficulty === 'intermediate' ? 'bg-blue-900 text-blue-200' :
                            motion.difficulty === 'advanced' ? 'bg-orange-900 text-orange-200' :
                            'bg-red-900 text-red-200'}`}
                          >
                            {motion.difficulty_display || motion.difficulty}
                          </span>
                        )}
                        {motion.info_slide && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-900 text-purple-200">
                            Info Slide
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Set the editing motion with proper data for editing
                          const theme_id = typeof motion.theme === 'object' ? motion.theme.id : 
                            (motion.theme_id ? motion.theme_id : 0);
                          
                          setEditingMotion({
                            ...motion,
                            theme_id,
                            theme_name: "",
                            new_competition_name: ""
                          });
                          setThemeInputMode('select');
                          setCompetitionInputMode('select');
                          setShowEditMotionDialog(true);
                        }}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                      >
                        <Edit size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="hidden md:block md:w-7/12 p-6 overflow-auto">
          {selectedMotion ? (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">{selectedMotion.text}</h2>
                <div className="flex gap-2 mt-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-blue-900 text-blue-200">
                    {typeof selectedMotion.theme === 'string' ? selectedMotion.theme : selectedMotion.theme.name}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-700 text-gray-200">
                    {selectedMotion.competition_type}
                  </span>
                  {selectedMotion.difficulty && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium 
                      ${selectedMotion.difficulty === 'beginner' ? 'bg-green-900 text-green-200' : 
                      selectedMotion.difficulty === 'intermediate' ? 'bg-blue-900 text-blue-200' :
                      selectedMotion.difficulty === 'advanced' ? 'bg-orange-900 text-orange-200' :
                      'bg-red-900 text-red-200'}`}
                    >
                      {selectedMotion.difficulty_display || selectedMotion.difficulty}
                    </span>
                  )}
                </div>
              </div>
              
              {selectedMotion.info_slide && (
                <div className="mb-6">
                  <h3 className="text-md font-semibold text-gray-400 mb-2">Info Slide</h3>
                  <div className="p-4 rounded-md border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <p className="whitespace-pre-wrap">{selectedMotion.info_slide}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Created</p>
                  <p>{new Date(selectedMotion.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">ID</p>
                  <p>{selectedMotion.id}</p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-8">
                <Button
                  onClick={() => {
                    // Set the editing motion with proper data for editing
                    const theme_id = typeof selectedMotion.theme === 'object' ? selectedMotion.theme.id : 
                      (selectedMotion.theme_id ? selectedMotion.theme_id : 0);
                    
                    setEditingMotion({
                      ...selectedMotion,
                      theme_id,
                      theme_name: "",
                      new_competition_name: ""
                    });
                    setThemeInputMode('select');
                    setCompetitionInputMode('select');
                    setShowEditMotionDialog(true);
                  }}
                  variant="blue"
                >
                  <Edit size={16} className="mr-2" />
                  Edit Motion
                </Button>
                <Button 
                  onClick={() => {
                    setMotionToDelete(selectedMotion);
                    setShowDeleteAlert(true);
                  }}
                  variant="destructive"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Motion
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p>Select a motion from the list to view details.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Motion Dialog */}
      <Dialog open={showEditMotionDialog} onOpenChange={setShowEditMotionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Motion</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateMotion} className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="edit_theme" className="dark:text-white light:text-gray-800">Theme</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setThemeInputMode(themeInputMode === 'select' ? 'create' : 'select')}
                  className="text-xs"
                >
                  {themeInputMode === 'select' ? 'Create New Theme' : 'Select Existing Theme'}
                </Button>
              </div>
              
              {themeInputMode === 'select' ? (
                <select
                  id="edit_theme_id"
                  value={editingMotion?.theme_id || ""}
                  onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, theme_id: parseInt(e.target.value)} : null)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
                  required
                >
                  <option value="">-- Select a Theme --</option>
                  {themes.map(theme => (
                    <option key={theme.id} value={theme.id}>{theme.name}</option>
                  ))}
                </select>
              ) : (
                <Input 
                  type="text"
                  id="edit_theme_name"
                  value={editingMotion?.theme_name || ""}
                  onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, theme_name: e.target.value} : null)}
                  placeholder="Enter new theme name"
                  className="w-full dark:text-white light:text-gray-800"
                  required
                />
              )}
            </div>

            <div>
              <Label htmlFor="edit_text" className="dark:text-white light:text-gray-800">Motion Text</Label>
              <Textarea 
                id="edit_text"
                value={editingMotion?.text || ""}
                onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, text: e.target.value} : null)}
                placeholder="Enter the motion text..."
                className="w-full dark:text-white light:text-gray-800"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_info_slide" className="dark:text-white light:text-gray-800">Info Slide (optional)</Label>
              <Textarea 
                id="edit_info_slide"
                value={editingMotion?.info_slide || ""}
                onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, info_slide: e.target.value} : null)}
                placeholder="Enter additional context or information for this motion..."
                className="w-full dark:text-white light:text-gray-800"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">Additional context or background information for debaters.</p>
            </div>
            
            <div>
              <Label htmlFor="edit_difficulty" className="dark:text-white light:text-gray-800">Difficulty Level</Label>
              <select
                id="edit_difficulty"
                value={editingMotion?.difficulty || "intermediate"}
                onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, difficulty: e.target.value} : null)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">The difficulty level of this motion for debaters.</p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="edit_competition_type" className="dark:text-white light:text-gray-800">Competition Type</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCompetitionInputMode(competitionInputMode === 'select' ? 'create' : 'select')}
                  className="text-xs"
                >
                  {competitionInputMode === 'select' ? 'Create New Competition' : 'Select Existing Competition'}
                </Button>
              </div>
              
              {competitionInputMode === 'select' ? (
                <select
                  id="edit_competition_type"
                  value={editingMotion?.competition_type || "General"}
                  onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, competition_type: e.target.value} : null)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
                  required
                >
                  {competitions.map(comp => (
                    <option key={comp} value={comp}>{comp}</option>
                  ))}
                </select>
              ) : (
                <Input 
                  type="text"
                  id="edit_new_competition_name"
                  value={editingMotion?.new_competition_name || ""}
                  onChange={(e) => setEditingMotion(editingMotion ? {...editingMotion, new_competition_name: e.target.value} : null)}
                  placeholder="Enter new competition type"
                  className="w-full dark:text-white light:text-gray-800"
                  required
                />
              )}
            </div>
            
            <DialogFooter className="sm:justify-end">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditMotionDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin mr-2">⌛</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    Update Motion
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Theme Management Dialog */}
      <Dialog open={showThemeManagementDialog} onOpenChange={setShowThemeManagementDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Themes</DialogTitle>
          </DialogHeader>
          
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-4">
              <Input 
                type="text"
                value={newThemeName}
                onChange={(e) => setNewThemeName(e.target.value)}
                placeholder="Enter new theme name"
                className="dark:text-white light:text-gray-800"
              />
              <Button 
                onClick={handleAddTheme}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus size={16} className="mr-2" />
                Add
              </Button>
            </div>
            
            <div className="max-h-60 overflow-y-auto border border-gray-700 rounded-md">
              <table className="min-w-full divide-y dark:divide-gray-700 light:divide-gray-200">
                <thead style={{ backgroundColor: 'var(--secondary-bg)' }}>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Theme Name</th>
                    <th className="px-4 py-2 text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700 light:divide-gray-200">
                  {themes.map((theme) => (
                    <tr key={theme.id} className="hover:bg-gray-700 hover:bg-opacity-20">
                      <td className="px-4 py-2 whitespace-nowrap dark:text-white light:text-gray-800">{theme.id}</td>
                      <td className="px-4 py-2 dark:text-white light:text-gray-800">{theme.name}</td>
                      <td className="px-4 py-2 text-center">
                        <Button
                          onClick={() => handleDeleteTheme(theme.id)}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          title="Delete Theme"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter className="sm:justify-end">
            <Button 
              onClick={() => setShowThemeManagementDialog(false)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Competition Management Dialog */}
      <Dialog open={showCompetitionManagementDialog} onOpenChange={setShowCompetitionManagementDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Competition Types</DialogTitle>
          </DialogHeader>
          
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-4">
              <Input 
                type="text"
                value={newCompetitionName}
                onChange={(e) => setNewCompetitionName(e.target.value)}
                placeholder="Enter new competition type"
                className="dark:text-white light:text-gray-800"
              />
              <Button 
                onClick={handleAddCompetition}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus size={16} className="mr-2" />
                Add
              </Button>
            </div>
            
            <div className="max-h-60 overflow-y-auto border border-gray-700 rounded-md">
              <table className="min-w-full divide-y dark:divide-gray-700 light:divide-gray-200">
                <thead style={{ backgroundColor: 'var(--secondary-bg)' }}>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Competition Type</th>
                    <th className="px-4 py-2 text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700 light:divide-gray-200">
                  {competitions.map((comp) => (
                    <tr key={comp} className="hover:bg-gray-700 hover:bg-opacity-20">
                      <td className="px-4 py-2 dark:text-white light:text-gray-800">{comp}</td>
                      <td className="px-4 py-2 text-center">
                        <Button
                          onClick={() => handleDeleteCompetition(comp)}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          title="Delete Competition Type"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter className="sm:justify-end">
            <Button 
              onClick={() => setShowCompetitionManagementDialog(false)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* New Motion Dialog */}
      <Dialog open={showNewMotionForm} onOpenChange={setShowNewMotionForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Motion</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddMotion} className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="theme" className="dark:text-white light:text-gray-800">Theme</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setThemeInputMode(themeInputMode === 'select' ? 'create' : 'select')}
                  className="text-xs"
                >
                  {themeInputMode === 'select' ? 'Create New Theme' : 'Select Existing Theme'}
                </Button>
              </div>
              
              {themeInputMode === 'select' ? (
                <select
                  id="theme_id"
                  value={newMotion.theme_id}
                  onChange={(e) => setNewMotion({...newMotion, theme_id: e.target.value})}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
                  required
                >
                  <option value="">-- Select a Theme --</option>
                  {themes.map(theme => (
                    <option key={theme.id} value={theme.id}>{theme.name}</option>
                  ))}
                </select>
              ) : (
                <Input 
                  type="text"
                  id="theme_name"
                  value={newMotion.theme_name}
                  onChange={(e) => setNewMotion({...newMotion, theme_name: e.target.value})}
                  placeholder="Enter new theme name"
                  className="w-full dark:text-white light:text-gray-800"
                  required
                />
              )}
              <p className="text-xs text-gray-500 mt-1">A category or subject area for the motion.</p>
            </div>

            <div>
              <Label htmlFor="text" className="dark:text-white light:text-gray-800">Motion Text</Label>
              <Textarea 
                id="text"
                value={newMotion.text}
                onChange={(e) => setNewMotion({...newMotion, text: e.target.value})}
                placeholder="Enter the motion text..."
                className="w-full dark:text-white light:text-gray-800"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="info_slide" className="dark:text-white light:text-gray-800">Info Slide (optional)</Label>
              <Textarea 
                id="info_slide"
                value={newMotion.info_slide}
                onChange={(e) => setNewMotion({...newMotion, info_slide: e.target.value})}
                placeholder="Enter additional context or information for this motion..."
                className="w-full dark:text-white light:text-gray-800"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">Additional context or background information for debaters.</p>
            </div>
            
            <div>
              <Label htmlFor="difficulty" className="dark:text-white light:text-gray-800">Difficulty Level</Label>
              <select
                id="difficulty"
                value={newMotion.difficulty}
                onChange={(e) => setNewMotion({...newMotion, difficulty: e.target.value})}
                className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">The difficulty level of this motion for debaters.</p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="competition_type" className="dark:text-white light:text-gray-800">Competition Type</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCompetitionInputMode(competitionInputMode === 'select' ? 'create' : 'select')}
                  className="text-xs"
                >
                  {competitionInputMode === 'select' ? 'Create New Competition' : 'Select Existing Competition'}
                </Button>
              </div>
              
              {competitionInputMode === 'select' ? (
                <select
                  id="competition_type"
                  value={newMotion.competition_type}
                  onChange={(e) => setNewMotion({...newMotion, competition_type: e.target.value})}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', borderColor: 'var(--border-color)' }}
                  required
                >
                  {competitions.map(comp => (
                    <option key={comp} value={comp}>{comp}</option>
                  ))}
                </select>
              ) : (
                <Input 
                  type="text"
                  id="new_competition_name"
                  value={newMotion.new_competition_name}
                  onChange={(e) => setNewMotion({...newMotion, new_competition_name: e.target.value})}
                  placeholder="Enter new competition type"
                  className="w-full dark:text-white light:text-gray-800"
                  required
                />
              )}
              <p className="text-xs text-gray-500 mt-1">The competition source of this motion, if applicable.</p>
            </div>

            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={submitting || loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin mr-2">⌛</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    Save Motion
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Motion Confirmation Dialog */}
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Motion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the motion <strong>"{motionToDelete?.text}"</strong>? 
              This action cannot be undone and will permanently remove the motion from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingMotion} onClick={() => { setShowDeleteAlert(false); setMotionToDelete(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteMotion}
              disabled={deletingMotion}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingMotion ? "Deleting..." : "Delete Motion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MotionsPanel;